// ════════════════════════════════════════════════════════════════════════════
//  Udisys Gestor Web  —  src/features/fiscal/services/notaFiscalService.ts
// ════════════════════════════════════════════════════════════════════════════

import { apiClient } from '@/lib/apiClient';
import type {
  NotaFiscalResumoDto,
  NotaFiscalDetalheDto,
  NotaFiscalRascunhoCriado,
  NotaFiscalConteudo,
  PaginadoResponseNFe,
  TransmitirNotaRequest,
  TransmitirNotaResponse,
  CancelarNotaRequest,
  CriarNotaFiscalRequest,
  TributacaoRequest,
  TributacaoResponse,
  ImpostosResumo,
  InutilizacaoFaixaContexto,
  InutilizarFaixaRequest,
  InutilizacaoFaixaResultado,
  NfeImportacaoResultado,
} from '../types';

const BASE = '/api/notas-fiscais';

// Transmitir e cancelar esperam a SEFAZ: a API aguarda o serviço fiscal por até 120 s. Com os
// 15 s padrão do cliente a tela acusava erro com a transmissão ainda em andamento no servidor.
const TIMEOUT_SEFAZ = 130_000;

export interface ListaNotasFiltros {
  busca?: string;
  situacao?: string;
  dtInicio?: string;
  dtFim?: string;
  ordenarPor?: 'emissao' | 'numero';
  decrescente?: boolean;
}

export const notaFiscalService = {
  /**
   * Lista paginada; busca (número, chave, CNPJ/CPF ou nome), situação, período de emissão
   * (yyyy-MM-dd) e ordenação são feitos no servidor.
   */
  async getAll(
    pagina        = 1,
    tamanhoPagina = 20,
    filtros: ListaNotasFiltros = {},
  ): Promise<PaginadoResponseNFe<NotaFiscalResumoDto>> {
    const { data } = await apiClient.get<PaginadoResponseNFe<NotaFiscalResumoDto>>(BASE, {
      params: {
        pagina, tamanhoPagina,
        busca: filtros.busca?.trim() || undefined,
        situacao: filtros.situacao && filtros.situacao !== 'todos' ? filtros.situacao : undefined,
        dtInicio: filtros.dtInicio || undefined,
        dtFim: filtros.dtFim || undefined,
        ordenarPor: filtros.ordenarPor,
        decrescente: filtros.decrescente,
      },
    });
    return data;
  },

  /**
   * Importa as NF-e emitidas em outro sistema: um .zip de XML (ou um .xml). Com `simular` nada é
   * gravado — a resposta diz o que seria importado, recusado e criado. Reenviar não duplica.
   */
  async importarEmitidas(arquivo: File, simular: boolean): Promise<NfeImportacaoResultado> {
    const form = new FormData();
    form.append('arquivo', arquivo);
    const { data } = await apiClient.post<NfeImportacaoResultado>(`${BASE}/importacao`, form, {
      params: { simular },
      headers: { 'Content-Type': 'multipart/form-data' },
      // Um zip mensal leva alguns segundos em produção; a API responde antes do limite do gateway.
      timeout: 240_000,
    });
    return data;
  },

  async getById(id: number): Promise<NotaFiscalDetalheDto> {
    const { data } = await apiClient.get<NotaFiscalDetalheDto>(`${BASE}/${id}`);
    return data;
  },

  async transmitir(id: number, req: TransmitirNotaRequest = {}): Promise<TransmitirNotaResponse> {
    const { data } = await apiClient.post<TransmitirNotaResponse>(`${BASE}/${id}/transmitir`, req, { timeout: TIMEOUT_SEFAZ });
    return data;
  },

  async cancelar(id: number, req: CancelarNotaRequest): Promise<TransmitirNotaResponse> {
    const { data } = await apiClient.post<TransmitirNotaResponse>(`${BASE}/${id}/cancelar`, req, { timeout: TIMEOUT_SEFAZ });
    return data;
  },

  /** Carta de Correção Eletrônica de uma nota autorizada (texto de 15 a 1000 caracteres). */
  async cartaCorrecao(id: number, correcao: string): Promise<TransmitirNotaResponse> {
    const { data } = await apiClient.post<TransmitirNotaResponse>(
      `${BASE}/${id}/carta-correcao`, { correcao }, { timeout: TIMEOUT_SEFAZ });
    return data;
  },

  /** Inutiliza na SEFAZ o número reservado por uma nota que nunca foi autorizada. Irreversível. */
  async inutilizar(id: number, justificativa: string): Promise<TransmitirNotaResponse> {
    const { data } = await apiClient.post<TransmitirNotaResponse>(
      `${BASE}/${id}/inutilizar`, { justificativa }, { timeout: TIMEOUT_SEFAZ });
    return data;
  },

  /** Ambiente e série do emitente, para a inutilização de números avulsos. */
  async contextoInutilizacao(): Promise<InutilizacaoFaixaContexto> {
    const { data } = await apiClient.get<InutilizacaoFaixaContexto>(`${BASE}/inutilizacoes/contexto`);
    return data;
  },

  /** Inutiliza na SEFAZ uma faixa de números que ficaram sem nota. Irreversível. */
  async inutilizarFaixa(req: InutilizarFaixaRequest): Promise<InutilizacaoFaixaResultado> {
    const { data } = await apiClient.post<InutilizacaoFaixaResultado>(
      `${BASE}/inutilizacoes`, req, { timeout: TIMEOUT_SEFAZ });
    return data;
  },

  /** Baixa o XML autorizado (procNFe) e dispara o download no navegador. */
  async baixarXml(id: number, chave: string | null): Promise<void> {
    const { data } = await apiClient.get<Blob>(`${BASE}/${id}/xml`, { responseType: 'blob' });
    const url  = URL.createObjectURL(data);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${chave ?? `nfe-${id}`}-procNFe.xml`;
    link.click();
    URL.revokeObjectURL(url);
  },

  /** Baixa o XML de um evento da nota (procEventoNFe) — o arquivo da carta de correção. */
  async baixarXmlEvento(id: number, eventoId: number, chave: string | null): Promise<void> {
    const { data } = await apiClient.get<Blob>(`${BASE}/${id}/eventos/${eventoId}/xml`, { responseType: 'blob' });
    const url  = URL.createObjectURL(data);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${chave ?? `nfe-${id}`}-evento-${eventoId}-procEventoNFe.xml`;
    link.click();
    URL.revokeObjectURL(url);
  },

  /** Abre o impresso da carta de correção (PDF) em uma nova aba. */
  async abrirCartaCorrecao(id: number, eventoId: number): Promise<void> {
    const { data } = await apiClient.get<Blob>(`${BASE}/${id}/eventos/${eventoId}/pdf`, { responseType: 'blob' });
    const url = URL.createObjectURL(new Blob([data], { type: 'application/pdf' }));
    window.open(url, '_blank', 'noopener');
    // O navegador precisa da URL viva enquanto carrega o PDF na aba nova.
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  },

  /** Abre o DANFE (PDF) da nota autorizada em uma nova aba. */
  async abrirDanfe(id: number): Promise<void> {
    const { data } = await apiClient.get<Blob>(`${BASE}/${id}/danfe`, { responseType: 'blob' });
    const url = URL.createObjectURL(new Blob([data], { type: 'application/pdf' }));
    window.open(url, '_blank', 'noopener');
    // O navegador precisa da URL viva enquanto carrega o PDF na aba nova.
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  },

  async criar(req: CriarNotaFiscalRequest): Promise<NotaFiscalRascunhoCriado> {
    const { data } = await apiClient.post<NotaFiscalRascunhoCriado>(BASE, req);
    return data;
  },

  /** Fatura um pedido baixado: cria o rascunho da NF-e. 409 traz `notaId` da nota já existente. */
  async gerarDePedido(davId: number): Promise<{ id: number; totalItens: number; totalNota: number; avisos: string[] }> {
    const { data } = await apiClient.post(`${BASE}/de-pedido/${davId}`);
    return data;
  },

  /** Destinatário, itens, pagamento, duplicatas, lotes e totais — o que se confere no rascunho. */
  async getConteudo(id: number): Promise<NotaFiscalConteudo> {
    const { data } = await apiClient.get<NotaFiscalConteudo>(`${BASE}/${id}/conteudo`);
    return data;
  },

  /** Refaz a nota (rascunho ou rejeitada) pelo pedido de origem, com o cadastro atual. Mantém o número. */
  async atualizarDoPedido(id: number): Promise<{ id: number; totalItens: number; totalNota: number; avisos: string[] }> {
    const { data } = await apiClient.post(`${BASE}/${id}/atualizar-do-pedido`);
    return data;
  },

  /** Descarta um rascunho nunca transmitido; se veio de pedido, o pedido volta a poder ser faturado. */
  /** Rascunho no formato do formulário, para reabrir e editar (nota ainda não autorizada). */
  async getParaEdicao(id: number): Promise<CriarNotaFiscalRequest> {
    const { data } = await apiClient.get<CriarNotaFiscalRequest>(`${BASE}/${id}/edicao`);
    return data;
  },

  async atualizar(id: number, req: CriarNotaFiscalRequest): Promise<void> {
    await apiClient.put(`${BASE}/${id}`, req);
  },

  async descartarRascunho(id: number): Promise<void> {
    await apiClient.delete(`${BASE}/${id}`);
  },

  async getResumoImpostos(dtInicio?: string, dtFim?: string): Promise<ImpostosResumo> {
    const { data } = await apiClient.get<ImpostosResumo>(`${BASE}/resumo-impostos`, {
      params: { dtInicio, dtFim },
    });
    return data;
  },

  async getTributacao(params: TributacaoRequest): Promise<TributacaoResponse> {
    const { data } = await apiClient.get<TributacaoResponse>('/api/fiscal/tributacao', { params });
    return data;
  },
};
